<script setup lang="ts">
import { onMounted, onUnmounted, ref, toRef, watch } from 'vue'

import type { DropdownPanelEmits, DropdownPanelProps } from './types'

import { useFloating } from '@/shared/ui/floating'
import { FloatingLayer, OverlayPortal, useOverlayFocusReturn } from '@/shared/ui/overlay'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<DropdownPanelProps>(), {
  placement: 'bottom-start',
  offset: 4,
})
const emit = defineEmits<DropdownPanelEmits>()

const floating = ref<HTMLElement | null>(null)
const open = toRef(props, 'open')
const { floatingStyles } = useFloating(toRef(props, 'reference'), floating, {
  placement: props.placement,
  offset: props.offset,
  strategy: 'fixed',
})
const { setFocusScope, completeLeave } = useOverlayFocusReturn(open, {
  resolveFallback: resolveReferenceFocusTarget,
})

function resolveReferenceFocusTarget(): HTMLElement | null {
  const reference = props.reference
  if (!reference?.isConnected) return null
  const selector = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',')
  if (reference.matches(selector)) return reference
  return reference.querySelector<HTMLElement>(selector)
}

function isTopLayer(): boolean {
  const layer = floating.value
  // Overlay order is written directly to the DOM by the stack and can change while this element
  // stays mounted. Read it for every interaction instead of caching a non-reactive dataset value.
  const order = Number(layer?.dataset.overlayOrder ?? -1)
  if (!layer || order < 0) return false
  return ![...document.querySelectorAll<HTMLElement>('[data-overlay-order]')].some(
    (candidate) => Number(candidate.dataset.overlayOrder) > order,
  )
}

function requestClose(): void {
  if (!props.open) return
  emit('update:open', false)
  emit('close')
}

function handleOutsidePointer(event: PointerEvent): void {
  if (!props.open || !isTopLayer()) return
  const path = event.composedPath()
  if (
    (floating.value && path.includes(floating.value)) ||
    (props.reference && path.includes(props.reference))
  ) {
    return
  }
  requestClose()
}

function handleEscape(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.repeat || !props.open || !isTopLayer()) return
  event.preventDefault()
  event.stopPropagation()
  requestClose()
}

watch(floating, setFocusScope, { flush: 'sync' })
watch(
  open,
  (isOpen, wasOpen) => {
    if (!isOpen && wasOpen) completeLeave()
  },
  { flush: 'post' },
)

onMounted(() => {
  document.addEventListener('pointerdown', handleOutsidePointer, true)
  document.addEventListener('keydown', handleEscape, true)
})
onUnmounted(() => {
  document.removeEventListener('pointerdown', handleOutsidePointer, true)
  document.removeEventListener('keydown', handleEscape, true)
})
</script>

<template>
  <OverlayPortal>
    <FloatingLayer
      v-if="open"
      v-model:element="floating"
      v-bind="$attrs"
      kind="dropdown"
      :style="floatingStyles"
    >
      <slot />
    </FloatingLayer>
  </OverlayPortal>
</template>
